/**
 * Fixture for the review widget — shape of morning_review's structured
 * output (mcp/server.ts morningReviewTool): tenant, since, totals,
 * sessions (sessionView + machineName + first/last lines), and the
 * overnight jobs (jobView + scheduleName).
 */
import {
  JOB_IDS,
  LIVE_SESSION_ID,
  MACHINES,
  NEEDS_HUMAN_SCREEN,
  SCHEDULE_IDS,
  hoursAgo,
  minAgo,
} from "./seed";

export function reviewFixture(): Record<string, unknown> {
  return {
    tenant: { kind: "personal" },
    teamRef: null,
    since: hoursAgo(18),
    totals: {
      sessions: 4,
      inputTokens: 700_000,
      outputTokens: 139_000,
      failedJobs: 1,
    },
    sessions: [
      {
        sessionId: LIVE_SESSION_ID,
        agent: "email-triage",
        machineId: MACHINES.mini.machineId,
        machineName: MACHINES.mini.name,
        status: "running",
        startedAt: minAgo(14),
        endedAt: null,
        errorMessage: null,
        lastSeq: 47,
        inputTokens: 48_200,
        outputTokens: 9_400,
        cacheReadTokens: 512_000,
        cacheWriteTokens: 38_000,
        needsHuman: true,
        needsHumanScreen: NEEDS_HUMAN_SCREEN,
        model: null,
        effort: null,
        overBudget: false,
        firstLine: "user: Sweep the overnight inbox and flag anything urgent",
        lastAssistantLine:
          "3 drafts ready — asking before anything sends.",
      },
      {
        sessionId: "ses-9b21-demo",
        agent: "morning-brief",
        machineId: MACHINES.mini.machineId,
        machineName: MACHINES.mini.name,
        status: "ended",
        startedAt: minAgo(160),
        endedAt: minAgo(151),
        errorMessage: null,
        lastSeq: 88,
        inputTokens: 214_000,
        outputTokens: 52_000,
        cacheReadTokens: 940_000,
        cacheWriteTokens: 61_000,
        needsHuman: false,
        needsHumanScreen: null,
        model: null,
        effort: "medium",
        overBudget: false,
        firstLine: "user: Compile the morning brief for today",
        lastAssistantLine:
          "Brief delivered — 6 items, 2 need your eyes before 9am.",
      },
      {
        sessionId: "ses-4c08-demo",
        agent: "email-triage",
        machineId: MACHINES.mini.machineId,
        machineName: MACHINES.mini.name,
        status: "ended",
        startedAt: minAgo(309),
        endedAt: minAgo(297),
        errorMessage: null,
        lastSeq: 132,
        inputTokens: 350_000,
        outputTokens: 64_000,
        cacheReadTokens: 1_300_000,
        cacheWriteTokens: 84_000,
        needsHuman: false,
        needsHumanScreen: null,
        model: null,
        effort: null,
        overBudget: false,
        firstLine: "schedule: Nightly inbox sweep (01:30)",
        lastAssistantLine:
          "Filed 31 messages, archived 12 — nothing urgent overnight.",
      },
      {
        sessionId: "ses-77de-demo",
        agent: "health-sync",
        machineId: MACHINES.air.machineId,
        machineName: MACHINES.air.name,
        status: "failed",
        startedAt: minAgo(228),
        endedAt: minAgo(226),
        errorMessage: "machine went offline mid-run",
        lastSeq: 9,
        inputTokens: 87_800,
        outputTokens: 13_600,
        cacheReadTokens: 210_000,
        cacheWriteTokens: 9_000,
        needsHuman: false,
        needsHumanScreen: null,
        model: "haiku",
        effort: "low",
        overBudget: false,
        firstLine: "schedule: Health data sync (03:00)",
        lastAssistantLine: null,
      },
    ],
    jobs: [
      {
        jobId: JOB_IDS.heldBrief,
        agentName: "morning-brief",
        prompt:
          "Compile the morning brief: overnight runs, today's calendar, " +
          "and anything that needs a human.",
        source: "mcp",
        scheduleId: null,
        scheduleName: null,
        state: "queued",
        sessionId: null,
        createdAt: minAgo(6),
        deliveredAt: null,
        error: null,
        attempts: 0,
        mode: "message",
        loopInterval: null,
        model: null,
        effort: "medium",
        alwaysRun: false,
        holdReason: "budget",
      },
      {
        jobId: JOB_IDS.failedHealth,
        agentName: "health-sync",
        prompt:
          "Pull last night's sleep and recovery data, then update the " +
          "health log and weekly rollup.",
        source: "schedule",
        scheduleId: SCHEDULE_IDS.health,
        scheduleName: "Health data sync",
        state: "failed",
        sessionId: "ses-77de-demo",
        createdAt: minAgo(230),
        deliveredAt: minAgo(229),
        error: "machine offline — delivery timed out after 3 attempts",
        attempts: 3,
        mode: "message",
        loopInterval: null,
        model: "haiku",
        effort: "low",
        alwaysRun: true,
        holdReason: null,
      },
      {
        jobId: JOB_IDS.deliveredSweep,
        agentName: "email-triage",
        prompt:
          "Sweep the overnight inbox. Flag anything urgent, draft replies " +
          "for review, and file the rest.",
        source: "schedule",
        scheduleId: SCHEDULE_IDS.sweep,
        scheduleName: "Nightly inbox sweep",
        state: "delivered",
        sessionId: "ses-4c08-demo",
        createdAt: minAgo(310),
        deliveredAt: minAgo(309),
        error: null,
        attempts: 1,
        mode: "message",
        loopInterval: null,
        model: null,
        effort: null,
        alwaysRun: false,
        holdReason: null,
      },
    ],
  };
}
